import { DAY, HOUR, MINUTE, SECOND } from '~/constants/date';
import {
  format,
  isAfter as isAfterFns,
  isBefore as isBeforeFns,
  isSameDay as isSameDayFns,
  setHours as setHoursFns,
  setMinutes as setMinutesFns,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subSeconds,
  subYears,
} from 'date-fns';
import { enUS, ja, ko, zhCN } from 'date-fns/locale';
import { padStart } from 'lodash';
import moment from 'moment-timezone';
import { __, always, curry, identity, ifElse, includes, lte, map, T } from 'ramda';
import cond from 'ramda/es/cond';
import equals from 'ramda/es/equals';
import pipe from 'ramda/es/pipe';
import { Maybe } from 'ramda-fantasy';

import { isNumber } from './fnUtil/fnUtil';

enum WeekDay {
  Monday = '1',
  Tuesday = '2',
  Wednesday = '3',
  Thursday = '4',
  Friday = '5',
  Saturday = '6',
  Sunday = '0',
}

const locales = { ko, en: enUS, ja, zh: zhCN };

const formatDate = curry((formatString: string, date: Date | number) => {
  return format(date, formatString, {
    locale: locales[window.__localeId__],
  });
});

const convertMomentToDate = (moment) => {
  return new Date(
    moment.year(),
    moment.month(),
    moment.date(),
    moment.hours(),
    moment.minutes(),
    moment.seconds(),
    moment.milliseconds()
  );
};

export const isWeekend = (timezoneName: string) => {
  return includes(moment.tz(timezoneName).format('e'))([WeekDay.Saturday, WeekDay.Sunday]);
};

export const startOf = curry((unit: 'day' | 'hour' | 'minute', timestamp: number | Date) => {
  return moment(timestamp).startOf(unit).valueOf();
});

const timestampPerUnit = {
  day: DAY,
  hour: HOUR,
  minute: MINUTE,
};

export const toTimestamp = (value: string) => {
  return new Date(value).getTime();
};

export const isInTimeUnit = curry((unit: 'day' | 'hour' | 'minute', value: number, other: number) => {
  const start = startOf(unit, value);
  const end = timestampPerUnit[unit] + start;
  return start <= other && other < end;
});

export const isWithinNow = (beginAt: string, endAt: string) => {
  return moment().isBetween(beginAt, endAt);
};

const fixMoment = (dateOrMoment) => {
  return moment.isMoment(dateOrMoment) ? dateOrMoment : moment(dateOrMoment);
};

export const isSameUnit = curry((unit, d1, d2) => {
  return fixMoment(d1).isSame(fixMoment(d2), unit);
});

export const isSameFromNow = (unit: 'year' | 'day', time: number, timezoneName: string) => {
  return moment.tz(timezoneName).isSame(time, unit);
};

const _getHourFromString = (string) => string.split(':')[0];

const _getMinuteFromString = (string) => string.split(':')[1];

const getHourMinuteFromMoment = (momentObj) => momentObj.format('HH:mm');

const getMomentFromString = (string) =>
  moment().set('hour', _getHourFromString(string)).set('minute', _getMinuteFromString(string));

export const getPastTime = (timestamp) => {
  return new Date().getTime() - timestamp;
};

export const getReconnectDelay = (startTime: number): Maybe<number> => {
  return pipe<any, any, any, any>(
    getPastTime,
    cond<any, any>([
      [lte(__, 5 * MINUTE), () => Maybe.of(10 * SECOND)],
      [lte(__, 60 * MINUTE), () => Maybe.of(MINUTE)],
      [lte(__, DAY), () => Maybe.of(10 * MINUTE)],
      [T, Maybe.Nothing],
    ]),
    map((value: number) => Math.ceil((value * Math.random()) / 1000) * 1000)
  )(startTime);
};

const padTowZero = (string) => padStart(string, 2, '0');

export const formatDuration = (timestamp) => {
  const d = moment.duration(timestamp);
  return `${padTowZero(d.hours())}:${padTowZero(d.minutes())}:${padTowZero(d.seconds())}`;
};

export const current = () => {
  return moment();
};

export const addFromCurrent = (value, unit) => {
  return current().add(value, unit);
};

export const isAfter = curry((date1: Date | number, date2: Date | number) => {
  return isAfterFns(date1, date2);
});

export const isAfterFromNow = (time: Date | number) => {
  return Date.now() < time;
};

export const isBefore = curry((date1: Date | number, date2: Date | number) => {
  return isBeforeFns(date1, date2);
});

export const isBeforeFromNow = (time) => {
  return isBeforeFns(time, new Date());
};

export const isSameDay = (time1: Date | number, time2: Date | number) => {
  return isSameDayFns(time1, time2);
};

export const addMinutes = (date: Date | number, amount: number) => {
  return amount * MINUTE + (date instanceof Date ? date.valueOf() : date);
};

export const subtractFromNow = (amount: number, unit: string) => {
  return cond([
    [equals('year'), always(subYears)],
    [equals('month'), always(subMonths)],
    [equals('day'), always(subDays)],
    [equals('hour'), always(subHours)],
    [equals('minute'), always(subMinutes)],
    [equals('second'), always(subSeconds)],
  ])(unit)(new Date(), amount);
};

export const assignTime = (now, time) => {
  const [hour, minute] = time.split(':');
  return now.clone().hour(hour).minute(minute);
};

const getOrMakeAfterTime = (pivot, other) => {
  return pivot.isSameOrAfter(other) ? other.add(1, 'day') : other;
};

export const getNearTimeTimestamp = (begin, end, timezoneName) => {
  const now = moment.tz(timezoneName).startOf('minute');
  const beginMoment = getOrMakeAfterTime(now, assignTime(now, begin));
  const endMoment = getOrMakeAfterTime(now, assignTime(now, end).endOf('minute'));
  return beginMoment.isBefore(endMoment) ? +beginMoment : +endMoment;
};

// now는 test 용 변수
export const isInTime = (beginTime, endTime, timezoneName, now = null) => {
  now = now || moment.tz(timezoneName);
  const begin = assignTime(now, beginTime);
  const end = assignTime(now, endTime);
  return begin.isAfter(end)
    ? now.isBetween(begin, end.add(1, 'day'), null, '[)')
    : now.isBetween(begin, end, null, '[)');
};

const setHours = curry((hours: number, date: Date | number) => {
  return pipe(
    ifElse(isNumber, (ts) => new Date(ts), identity),
    (date) => setHoursFns(date, hours)
  )(date);
});

const setMinutes = curry((minutes: number, date: Date | number) => {
  return pipe(
    ifElse(isNumber, (ts) => new Date(ts), identity),
    (date) => setMinutesFns(date, minutes)
  )(date);
});

let isSet = false;
const setDefaultTimeThreshold = () => {
  if (isSet) {
    return;
  }
  moment.relativeTimeRounding(Math.floor);
  moment.relativeTimeThreshold('s', 60);
  moment.relativeTimeThreshold('m', 60);
  moment.relativeTimeThreshold('h', 24);
  moment.relativeTimeThreshold('d', 30);
  moment.relativeTimeThreshold('M', 12);
  isSet = true;
};

const trimTimeOnVacation = (begin: string, end: string) => {
  if (begin.endsWith('00:00') && end.endsWith('23:59')) {
    begin = begin.replace(' 00:00', '');
    end = end.replace(' 23:59', '');
  }
  return [begin, end];
};

const dateUtil = {
  convertMomentToDate,
  formatDate,
  getHourMinuteFromMoment,
  getMomentFromString,
  isSameUnit,
  isSameFromNow,
  isWithinNow,
  startOf,
  current,
  addFromCurrent,
  isAfterFromCurrent: isAfterFromNow,
  isBeforeFromCurrent: isBeforeFromNow,
  isSameDay,
  subtractFromNow,
  setHours,
  setMinutes,
  setDefaultTimeThreshold,
  trimTimeOnVacation,
};

export default dateUtil;
